/**
 * Arena view — the live 1v1 duel, rendered.
 *
 * A trimmed sibling of `location-view.ts` in exactly the way that file is a
 * trimmed sibling of `index.ts`: same sim, scene, camera and control modules,
 * none of the settlement's panels. There is nothing to talk to here and
 * nothing to search — only an opponent, a clock, and the arc in front of you.
 *
 * ─── What this closes ───────────────────────────────────────────────────────
 * Until now the client had no live-combat surface at all. The arena queue, the
 * offer, the lobby and the 8-second countdown were all built and working, and
 * at the moment the server pushed `LIVE` the modal simply hid itself and the
 * player was left standing in the settlement. The duel ran to completion in a
 * Colyseus room nobody had joined: against a bot that meant the bot walked up
 * to a motionless character and killed it, and `concludeMatch` then applied a
 * guaranteed downing — emptying the player's HP, clearing their carried bag
 * and sending them to the Med-Center, with no fight and no explanation.
 *
 * ─── Server-authoritative positions, local prediction for one body ──────────
 * The player's own body is driven by the local sim and streamed up at ~10 Hz,
 * exactly like the settlement — that is what keeps movement responsive. The
 * OPPONENT is drawn purely from the room's synced state, interpolated between
 * patches. We never simulate the opponent locally: there is no second sim to
 * reconcile, and a predicted opponent would be wrong in precisely the moments
 * that matter (the ones where someone is shooting).
 *
 * HP is the server's alone, for both bodies. The local sim has no combat.
 *
 * ─── The freeze is enforced twice, and that is deliberate ───────────────────
 * The room rejects input until `liveAt`, and this view also refuses to feed
 * movement into the sim while `freezeRemaining > 0`. The server's copy is the
 * authority; the client's copy is what stops the player from walking somewhere
 * during the countdown and being snapped back when the first accepted `move`
 * lands.
 */
import * as THREE from 'three';

import { ARENA_CAMERA, DEFAULT_CAMERA } from './types';
import type { WorldData } from './types';
import { createBaseSim } from './sim/base-sim';
import { buildScene } from './render/scene';
import { attachWorldModels } from './render/model-attach';
import { buildPlayerShadow } from './render/player-shadow';
import { createPlayerAnimator, type AnimatorEquipmentSource } from './render/character-animations';
import { createNameTags, playerDisplayName } from './render/name-tags';
import { CameraController } from './render/camera-controller';
import { Picker } from './render/picking';
import { createPlayerControls } from './player-controls';
import { keyName, trackHeldKeys } from './key-input';
import { createAimArc } from './render/aim-arc';
import { createMuzzleFlashPool } from './render/sprite-fx';
import { createShotFeedback } from './render/shot-feedback';
import { createFloatingNumbers } from './render/floating-numbers';
import { createCombatBars } from './render/combat-bars';
import { injectStyles } from './ui/styles';
import { createDuelHud } from './ui/duel-hud';
import { buildHotbarSlots, createHotbar, hotbarIndexForKey } from './ui/hotbar';
import { createMixer } from '../audio/mixer';
import { createWeaponSound } from '../audio/weapon-sound';
import { createDamageSound } from '../audio/damage-sound';
import { createUiSounds } from '../audio/ui-sounds';
import { TRACKS } from '../audio/tracks';
import { loadCharacterModel } from '../character-select/load-model';
import { CHARACTERS } from '../character-select/characters';
import { gameStateStore } from '../state/game-state';
import type { EquippedItemSummary } from '../state/game-state.types';
import { createHealWatcher } from '../state/heal-feed';
import { isFireKey } from '../state/control-settings';
import { authStore } from '../../core/state/auth-store';
import {
  battlegroundStateStore, joinDuel, leaveDuel, sendAttack, sendForfeit, sendMove,
  sendSelectSlot, sendUseItem, onAttackResult, onHealResult, onOutOfAmmo,
  onHealStarted, onHealCancelled,
} from '../state/battleground-state';
import type { RoomPlayer, RoomVec3 } from '../state/battleground-state.types';
import type { ArenaMatchView } from '../state/arena-state.types';
import { arenaStateStore, remainingMs, reportArenaLoaded } from '../state/arena-state';

/**
 * How fast the opponent's drawn position chases the last synced one.
 *
 * Exponential smoothing rather than a fixed lerp step: patches arrive at the
 * room's tick rate, not the render rate, so a fixed step would either lag on a
 * slow frame or overshoot on a fast one. The constant is a half-life in
 * seconds — low enough that a sprinting opponent is never visibly behind,
 * high enough that a dropped patch does not read as a stutter.
 */
const OPPONENT_SMOOTH_HALFLIFE = 0.08;

/** Metres from the character's own origin to the muzzle — matches `index.ts`. */
const MUZZLE_HEIGHT = 1.15;

/**
 * How far forward of the player's own origin the flash sits, metres.
 *
 * Roughly the end of a shouldered long arm on this chibi rig — far enough
 * that the flash is clear of the body. It used to live in `index.ts` next to
 * `MUZZLE_HEIGHT` and went with the settlement's combat removal; the height
 * stayed behind there because the floating heal numbers still want it.
 *
 * Derived from the player's TRANSFORM rather than read off the weapon's bone,
 * which is the same call the settlement made and for the same reason: the
 * weapon model loads asynchronously and is parented deep inside the rig, so a
 * bone lookup silently produces nothing for the first shots after a gear swap.
 * An offset along the facing direction is stable, always available, and
 * accurate enough for a 55 ms flash.
 */
const MUZZLE_FORWARD = 0.55;

/**
 * Below this, a patch counts as "they are standing still".
 *
 * Not zero: positions arrive as floats from a server that is itself integrating
 * movement, so an exactly-identical pair of patches is the exception rather
 * than the rule even for a motionless opponent.
 */
const MOVE_EPSILON = 1e-4;

/**
 * How long a position change keeps the opponent "moving", in seconds.
 *
 * Longer than the gap between two patches (50 ms) with room for a dropped
 * one, and short enough that a bot stopping in its firing band settles into
 * idle without visibly sliding on a run cycle.
 */
const OPPONENT_MOVE_HOLD_S = 0.18;

/**
 * The body used when the room has not told us who we are fighting.
 *
 * Reached only in the frames before the first state patch arrives, or against
 * a server too old to sync `presetId`. It used to be the ONLY body an opponent
 * ever had — see `opponentEquipment` below for what replaced that.
 */
const FALLBACK_OPPONENT_MODEL_URL = '/models/char-1.glb';


export interface ArenaViewHandles {
  teardown: () => void;
}

/**
 * DEV-only inspection hook — `window.__duel` while a duel is mounted.
 *
 * Same shape and the same gate as `minimap.ts`'s `__bakeMinimap` and
 * `index.ts`'s `DebugOverlay`: built only under `import.meta.env.DEV`, so
 * Vite's tree-shaker drops it from a production bundle entirely rather than
 * shipping a dormant global.
 *
 * It exists because the three things this view was rebuilt to fix are
 * invisible from the DOM. The opponent's position lives in a WebGL scene, the
 * held weapon lives in the room's synced state, and the round count is a
 * number nothing else writes down — so `scripts/duel_probe.mjs` can only
 * assert "the bot no longer teleports" and "the shotgun fires for 50" if the
 * view hands them over. Read-only: nothing here can change what the room does.
 */
interface DuelDevWindow extends Window {
  __duel?: {
    /** Our own row in the room's synced state — weapon, ammo, arc, HP. */
    self(): RoomPlayer | null;
    /** The opponent's row, for measuring how far they move per patch. */
    opponent(): RoomPlayer | null;
    /** Ms left on the pre-duel countdown. Nobody moves until it reads 0 — a probe measuring motion must wait for it. */
    freezeRemaining(): number;
    /** The SMOOTHED draw position, which is what a human actually sees move. */
    opponentDrawn(): { x: number; z: number };
    /** The `moving` flag the opponent's animator was last fed: sample it per frame to catch idle/run flicker. */
    opponentMoving(): boolean;
    /** Puts the result card up for an outcome — the win and draw cards cannot be reached on demand otherwise. */
    showResult(outcome: 'win' | 'lose' | 'draw'): void;
    /** The ten cells, as the bar currently has them. */
    hotbar(): ReturnType<typeof buildHotbarSlots>;
    /** Is the OUT OF AMMO notice up right now? */
    outOfAmmoShown(): boolean;
    /** The heal cast bar — shown, ms left, and its label. */
    healing(): { shown: boolean; remainingMs: number; label: string };
    /**
     * Every skinned body in the scene, by ring radius and right-hand bone
     * position. Sample it twice: a hand that has not moved belongs to a body
     * whose mixer nobody is advancing. See the implementation's note.
     */
    crowd(): { radius: number; hand: [number, number, number] }[];
    /**
     * What the local BODY currently has in its hand, or null for empty hands.
     *
     * Read off the scene graph, not off the store: the whole question is
     * whether the model followed the room, so asking the thing that decides
     * the model would prove nothing.
     */
    heldWeaponModel(): { key: string } | null;
  };
}

export function startArenaView(
  root: HTMLElement,
  world: WorldData,
  match: ArenaMatchView,
  onExit: () => void,
  playerModel?: THREE.Object3D,
): () => void {
  const cleanups: Array<() => void> = [];
  const removeStyles = injectStyles();
  let torndown = false;

  const selfId = match.you.userId;
  const opponentId = match.opponent.userId;

  root.innerHTML = '';
  const container = document.createElement('div');
  container.className = 'bv-root';
  root.appendChild(container);

  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth || 1, container.clientHeight || 1);
  // The resize handler below passes `updateStyle: false`, so the pixel size
  // this call wrote into the canvas's inline style would otherwise stick: a
  // rotated phone kept a landscape-sized canvas and showed a stretched crop.
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  container.appendChild(renderer.domElement);
  // Your own shot's kick: a render-only shake and a corner blur (render/shot-feedback.ts).
  const shotFeedback = createShotFeedback(container);

  const sim = createBaseSim(world);
  const sceneHandles = buildScene(world, playerModel);

  // ─── Without this the arena is bare ground ──────────────────────────────
  // `buildScene` returns an EMPTY `THREE.Group` for any object carrying a
  // `modelUrl` (see `buildObjectVisual`) — positioned and pickable, but with
  // nothing in it — because the real GLB is loaded asynchronously and parented
  // into that group afterwards. Every one of `arena_pit`'s 86 barriers and all
  // its cover has a `modelUrl`, so skipping this call rendered a duel on an
  // empty field: no walls, no cover, nothing to fight around. It looked like a
  // camera problem (an unbroken horizon) rather than a missing load, which is
  // exactly why it survived the first pass.
  //
  // `location-view.ts` legitimately omits it — `buildLocationWorld` places
  // primitive-kit loot with no `modelUrl` at all — so its shape is not
  // precedent here.
  const npcAnimators = attachWorldModels(world, sceneHandles, () => torndown);
  cleanups.push(() => npcAnimators.dispose());

  // ─── The body holds what the HOTBAR chose, not what the sheet says ───────
  //
  // `createPlayerAnimator`'s default source reads
  // `equipment.slots.PRIMARY ?? .SECONDARY` — the launch loadout. The hotbar
  // changes what is in the fighter's hands mid-duel and the animator had no
  // way to know: pressing 2 swapped the weapon the ROOM resolves hits with
  // while the body went on holding the rifle, and pressing 0 emptied the hands
  // server-side while the model stayed. Janeks: *"switching weapons does not
  // switch it on the player body."*
  //
  // Fixed through the injection seam the animator already has — the same one
  // the opponent's gear uses below — rather than by teaching it about duels:
  // the source narrows the real sheet down to the one slot the room says is
  // held. Armour and garments pass through untouched, so a swap changes the
  // hands and nothing else.
  let lastHeldSlot: string | undefined;
  const heldWeaponSource: AnimatorEquipmentSource = {
    slots: () => {
      const sheet = gameStateStore.get().equipment?.slots as
        Partial<Record<string, EquippedItemSummary>> | undefined;
      const held = selfPlayer()?.weaponSlot;
      // Before the first patch the room has no opinion yet, so the sheet's own
      // PRIMARY-then-SECONDARY rule stands — which is what the room will
      // choose anyway (`resolvePlayerWeapons`).
      if (!sheet || !held) return sheet;
      // Destructured out by name, with the lint convention's underscore: the
      // point is the REST, and naming the two weapon slots is what makes
      // "everything except the hands passes through" readable.
      const { PRIMARY: _p, SECONDARY: _s, ...rest } = sheet;
      if (held === 'UNARMED') return rest;
      return { ...rest, [held]: sheet[held] };
    },
    subscribe: (onChange) => {
      const unsubSheet = gameStateStore.subscribe(onChange);
      // Room patches arrive at the simulation rate. Re-deriving the whole
      // loadout thirty times a second to discover it has not changed is work
      // for nothing, so the held slot is compared first and the animator is
      // disturbed only when it actually moved.
      const unsubRoom = battlegroundStateStore.subscribe(() => {
        const held = selfPlayer()?.weaponSlot;
        if (held === lastHeldSlot) return;
        lastHeldSlot = held;
        onChange();
      });
      return () => { unsubSheet(); unsubRoom(); };
    },
  };

  const playerAnimator = createPlayerAnimator(sceneHandles.player, heldWeaponSource);
  cleanups.push(() => playerAnimator.dispose());

  // Spread, not a fallback: `WorldData.camera` is a PARTIAL override, so a map
  // that tunes only its zoom clamp must keep the defaults for everything else.
  // Same line as `index.ts:171`.
  //
  // `ARENA_CAMERA` goes LAST: the duel camera is pinned and fitted to the
  // bots' sight radius, and no map may loosen that (see its docstring).
  const cam = new CameraController(aspectOf(container), { ...DEFAULT_CAMERA, ...world.camera, ...ARENA_CAMERA });
  const picker = new Picker();
  const controls = createPlayerControls({
    container,
    canvas: renderer.domElement,
    picker,
    getCamera: () => cam.camera,
  });
  cleanups.push(() => controls.dispose());
  const reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');

  const playerShadow = buildPlayerShadow();
  sceneHandles.scene.add(playerShadow);

  // ---- the aim arc (checklist: "show line of shot, based on weapon stats") ----
  const aimArc = createAimArc();
  sceneHandles.scene.add(aimArc.object);
  cleanups.push(() => aimArc.dispose());

  // ---- muzzle flashes: pooled one-shots, fired from the attack path ----
  const muzzleFlashes = createMuzzleFlashPool();
  sceneHandles.scene.add(muzzleFlashes.object);
  cleanups.push(() => muzzleFlashes.dispose());
  cleanups.push(() => shotFeedback.dispose());

  // ---- audio ----
  //
  // Declared before the try so the rest of the view can call them
  // unconditionally — a build with no AudioContext leaves them as no-ops
  // rather than making every call site check whether sound exists.
  let uiErrorSound: () => void = () => {};
  let escalateMusic: () => void = () => {};
  let escalated = false;
  //
  // ─── The arena had NONE of this ────────────────────────────────────────────
  // Not a gap in the mix — there was no `AudioContext` in this view at all.
  // `startBaseView` creates the only mixer in the game, and the arena is its
  // own mounted view that does not go through it, so a duel ran in total
  // silence: no gunshot, no impact, no score. Janeks: *"Shotgun is not
  // shooting (no sound in arena)."* The apparatus was all shipped and all
  // unreachable — `index.ts`'s "no combat here" note says arena-view "uses
  // every part of it", and it used none.
  //
  // Wrapped exactly like `index.ts`'s: an AudioContext is not guaranteed to
  // exist (jsdom has none, and a browser may refuse when too many are open),
  // and losing sound must never take the duel down with it. Everything below
  // is null-checked rather than assumed.
  let weaponSound: ReturnType<typeof createWeaponSound> | null = null;
  try {
    const mixer = createMixer();
    cleanups.push(() => mixer.dispose());
    weaponSound = createWeaponSound(mixer);
    // Taking a hit, and the four interface sounds — `uiError` among them is
    // what an empty trigger plays.
    cleanups.push(createDamageSound(mixer));
    cleanups.push(createUiSounds(mixer));
    uiErrorSound = () => {
      if (!mixer.isRunning()) return;
      void mixer.playOnce(TRACKS.uiError.path, TRACKS.uiError.channel).catch(() => {});
    };

    // ─── The two PvP beds, finally wired ──────────────────────────────────
    // `TRACKS.pvpStealth` and `TRACKS.pvpAction` were authored, loop-wrapped,
    // measured and shipped, and both carried "NOT WIRED" in their own
    // docstrings — there was no view to play them in. This is that view.
    //
    // Stealth is the bed; action takes over once either fighter has been hit,
    // which is this mode's equivalent of `alerted` (that flag is PvE
    // detection and stays false for a whole duel). One-way: a duel that has
    // drawn blood does not calm back down.
    let stopMusic: (() => void) | null = null;
    let tornDownEarly = false;
    const playBed = (track: { path: string }): void => {
      void mixer.playLoop(track.path, 'music', 1, 3)
        .then((stop) => { if (tornDownEarly) stop(); else stopMusic = stop; })
        .catch(() => { /* a missing score is not worth a broken duel */ });
    };
    playBed(TRACKS.pvpStealth);
    escalateMusic = () => {
      if (escalated) return;
      escalated = true;
      stopMusic?.();
      stopMusic = null;
      playBed(TRACKS.pvpAction);
    };
    cleanups.push(() => { tornDownEarly = true; stopMusic?.(); });
  } catch {
    // No Web Audio here. `weaponSound` stays null and the two hooks below stay
    // no-ops; everything visual is unaffected.
  }

  // ---- floating damage/heal numbers + per-body HP/armour bars ----
  const floaters = createFloatingNumbers();
  sceneHandles.scene.add(floaters.object);
  cleanups.push(() => floaters.dispose());

  const bars = createCombatBars();
  sceneHandles.scene.add(bars.object);
  cleanups.push(() => bars.dispose());

  const nameTags = createNameTags(world);
  sceneHandles.scene.add(nameTags.object);
  const unsubNameTags = authStore.subscribe(() => {
    nameTags.setPlayerName(playerDisplayName(authStore.get().user));
  });
  cleanups.push(unsubNameTags, () => nameTags.dispose());

  // ---- the opponent's body ----
  //
  // Built as a second `createPlayerAnimator`, not an NPC animator: the
  // opponent IS a player, and only the player animator has a locomotion state
  // machine. An NPC animator would leave them sliding across the ground in a
  // standing pose.
  //
  // Loaded asynchronously and attached when it arrives; until then the duel
  // renders with one body rather than blocking the whole scene on a fetch.
  const opponentRoot = new THREE.Group();
  opponentRoot.visible = false;
  sceneHandles.scene.add(opponentRoot);
  let opponentAnimator: ReturnType<typeof createPlayerAnimator> | null = null;
  const opponentShadow = buildPlayerShadow();
  opponentShadow.visible = false;
  sceneHandles.scene.add(opponentShadow);

  // ─── The opponent's own body, own clothes, own weapon ───────────────────
  //
  // This used to load a hardcoded `/models/char-2.glb` and pass no equipment
  // source, which meant every opponent in every duel was the same unclothed
  // man holding a live copy of YOUR weapon (the animator defaults to the local
  // player's store — see `AnimatorEquipmentSource`). Both halves are fixed
  // here: the body comes from the room's `presetId`, and the gear from an
  // equipment source backed by the room's own fields for THAT fighter.
  //
  // Deferred rather than loaded up front, because the preset arrives with the
  // first state patch and not before. `opponentBodyRequested` makes it
  // once-only — `frame` runs sixty times a second and a flag is the whole
  // guard.
  /**
   * The opponent's gear, as the animator wants to see it.
   *
   * ─── Synthesised from the room, not fetched ─────────────────────────────
   * The animator reads `EquippedItemSummary`-shaped rows, which is what an
   * equipment sheet holds — and this client will never have the OPPONENT's
   * sheet. It does have the two things that actually drive what is on screen:
   * `weaponKey` (what is in their hands right now, already synced because the
   * hotbar changes it mid-duel) and `equippedKeys` (what they are wearing,
   * written once at spawn).
   *
   * So the rows are built from those. Only the fields the render path reads
   * are real — `def.key`, `def.kind`, `def.ammoType` — because those three are
   * genuinely all of it: `armsPoseFamilyFor` picks the hold from them, and the
   * garment/torso/headgear tables are keyed on `def.key` alone. The cast is
   * the honest way to say "this is a view of a sheet, not a sheet".
   *
   * `kind`/`ammoType` come from `weaponKind`/`weaponAmmoType`, which the room
   * syncs alongside the key for exactly this class of reason (the gunshot
   * sound needs the same two). Without them every opponent would hold every
   * weapon in the generic rifle pose.
   */
  const opponentEquipment: AnimatorEquipmentSource = {
    slots: () => {
      const them = battlegroundStateStore.get().snapshot.players.find((p) => p.userId !== selfId);
      if (!them) return undefined;
      const out: Record<string, EquippedItemSummary> = {};
      const row = (key: string, kind: string, ammoType: string | null) => ({
        def: { key, kind, ammoType },
      } as unknown as EquippedItemSummary);

      if (them.weaponKey) {
        // Which SLOT the room says it is in, so a sidearm reads as a sidearm.
        // `UNARMED` means bare hands and must land in neither slot — the pose
        // resolver treats an absent PRIMARY and SECONDARY as unarmed, which is
        // exactly right.
        const slot = them.weaponSlot === 'SECONDARY' ? 'SECONDARY' : 'PRIMARY';
        if (them.weaponSlot !== 'UNARMED') {
          out[slot] = row(them.weaponKey, them.weaponKind ?? 'FIREARM', them.weaponAmmoType ?? null);
        }
      }
      // Worn keys carry no slot across the wire, and none is needed: every
      // consumer looks the key up in its own table (GARMENT_KEYS,
      // TORSO_MODEL_BY_KEY, EQUIPMENT_MODEL_BY_KEY) rather than trusting the
      // slot it arrived in. Keyed by the item key itself so two garments never
      // collide on one slot name.
      for (const key of them.equippedKeys ?? []) {
        out[key] = row(key, 'ARMOUR', null);
      }
      return out;
    },
    // Re-render on any room patch. The animator's own change handler diffs the
    // resolved keys before touching a model, so a patch that moved only a
    // position costs a comparison and nothing else.
    subscribe: (onChange) => battlegroundStateStore.subscribe(onChange),
  };

  let opponentBodyRequested = false;
  const loadOpponentBody = (presetId: string | undefined): void => {
    if (opponentBodyRequested) return;
    opponentBodyRequested = true;
    const url = CHARACTERS.find((c) => c.id === presetId)?.modelUrl ?? FALLBACK_OPPONENT_MODEL_URL;
    void loadCharacterModel(url).then((model) => {
      if (torndown) return;
      opponentRoot.add(model);
      opponentAnimator = createPlayerAnimator(opponentRoot, opponentEquipment);
      cleanups.push(() => opponentAnimator?.dispose());
    }).catch((err) => {
      console.warn('[arena] opponent model failed to load', err);
    });
  };

  /**
   * Death is terminal and plays exactly once — see `PlayerAnimator.playDeath`.
   * A flag rather than a check on the animator because the trigger (`hp <= 0`)
   * stays true for every remaining frame of the duel.
   */
  let deathPlayed = false;
  /** The same, for the opponent: the loser's body should fall whichever of the two it is. */
  let opponentDeathPlayed = false;

  /** Drawn opponent position — smoothed toward the last synced one, see `OPPONENT_SMOOTH_HALFLIFE`. */
  const opponentDrawn = new THREE.Vector3(0, 0, 0);
  let opponentSeen = false;
  let opponentLastPos: RoomVec3 = { x: 0, y: 0, z: 0 };
  let opponentMoving = false;
  /** Seconds left before an opponent with no new position counts as stopped — see `OPPONENT_MOVE_HOLD_S`. */
  let opponentMoveHold = 0;

  // ---- HUD: clock, freeze countdown, both HP bars, forfeit ----
  const hud = createDuelHud(container, {
    selfName: playerDisplayName(authStore.get().user),
    opponentName: match.opponent.username,
    onForfeit: () => {
      sendForfeit();
    },
    onLeave: () => {
      doExit();
    },
  });
  cleanups.push(() => hud.dispose());

  // ---- the hotbar: what is in your hands, and what you can put there ----
  //
  // Rebuilt from the two sheets on every push rather than diffed: the bar is
  // ten cells of markup and the sheets change a handful of times a duel (an
  // equip, a bandage), so a diff would be machinery guarding nothing.
  const hotbar = createHotbar(container, {
    onSelectWeapon: (slot) => sendSelectSlot(slot),
    onUseItem: (userItemId) => sendUseItem(userItemId),
  });
  cleanups.push(() => hotbar.dispose());

  /**
   * The heal currently being channelled, or null.
   *
   * The NAME and the total duration come from the `healStarted` message; the
   * live countdown comes from the room's synced `healingRemaining` in the
   * frame loop. Neither alone is enough — the snapshot cannot say which item
   * is running, and the message cannot keep time.
   */
  let healing: { itemName: string; durationMs: number } | null = null;
  cleanups.push(onHealStarted((started) => {
    healing = { itemName: started.itemName, durationMs: started.durationMs };
    hotbar.setHealing({ ...healing, remainingMs: started.durationMs });
  }));
  cleanups.push(onHealCancelled(() => {
    healing = null;
    hotbar.setHealing(null);
    // The kit was NOT spent — the player attacked or swapped weapons mid-heal,
    // which is a decision they made, not a failure. Said plainly rather than
    // silently, because a bar that vanishes with no word reads as a bug.
    hotbar.flashError('Healing interrupted');
  }));

  const refreshHotbar = (): void => {
    const state = gameStateStore.get();
    hotbar.setSlots(buildHotbarSlots(state.equipment, state.inventory));
  };
  refreshHotbar();
  cleanups.push(gameStateStore.subscribe(refreshHotbar));

  const doExit = (): void => {
    if (torndown) return;
    void leaveDuel();
    onExit();
  };

  // ---- join the room, when there is a room to join ----
  //
  // ─── Why this waits instead of joining on mount ─────────────────────────
  // This view now mounts at LOBBY, before the room exists. Janeks: *"the
  // countdown of 'no-one is moving' is when the pvp map is already loaded.
  // not on the matchmaking ui."* The 8-second freeze used to burn on the
  // lobby modal while the player stood in the settlement, and the arena
  // mounted afterwards — so the hold that exists to let both fighters arrive
  // and look around was spent looking at a modal.
  //
  // The order is now: mount -> render -> report `arena:loaded` -> the server
  // mints the seats and starts the countdown -> join. `match.seat` is null
  // for the first stretch of this view's life, which is normal, not an error.
  //
  // This ordering also makes `colyseus-seat-reservation-15s` less likely to
  // bite rather than more: the slow step (loading the map) now happens with
  // no reservation outstanding, where before the seat was minted first and
  // the load ate into its TTL.
  let joined = false;
  const joinWhenSeated = (m: ArenaMatchView | null): void => {
    if (joined || torndown) return;
    // Id-matched: a stale push from a previous duel must never seat us here.
    if (!m || m.matchId !== match.matchId || !m.seat) return;
    joined = true;
    void joinDuel(m.seat.seatReservation, selfId, m.matchId);
  };
  joinWhenSeated(match);
  cleanups.push(arenaStateStore.subscribe((s) => joinWhenSeated(s.view.match)));

  const unsubDuel = battlegroundStateStore.subscribe((s) => {
    if (s.status === 'failed' && s.error) hud.showError(s.error);
    // Gated on the match id, not merely on `result` being set. A verdict left
    // in the store by the PREVIOUS duel — a reconnect, or a `refreshArena`
    // landing a `LIVE` push before `joinDuel` has cleared it — would otherwise
    // paint a result card over a fight that is still running. Matching on the
    // id makes the card structurally unable to belong to a different duel.
    if (s.result && s.result.matchId === match.matchId) hud.showResult(s.result.outcome);
  });
  cleanups.push(unsubDuel);

  // ---- combat feedback ----
  const unsubAttack = onAttackResult((result) => {
    // Blood drawn — the score lifts, whoever it was drawn by. See the two PvP
    // beds in the audio block above.
    if (result.hit) escalateMusic();

    // ─── THEIR gun makes a noise too ──────────────────────────────────────
    // Our own shot is fired from the local keypress (`fireAttack`) so it lands
    // without a round trip. The opponent's has no local press to hang off, so
    // it rides the broadcast — which is the right latency for it: a shot you
    // did not fire is a thing that already happened.
    //
    // Fired on hit AND miss, deliberately. `resolvePvpAttack` broadcasts both,
    // and a gun that is only audible when it connects teaches the player that
    // silence means safety.
    //
    // The weapon comes from the opponent's synced `PlayerState` — the reason
    // `weaponKey`/`weaponAmmoType` are on it at all. Without them the arena
    // could only ever sound its own player, which in a 1v1 is half the
    // gunfire missing.
    if (result.attackerId !== selfId) {
      const them = battlegroundStateStore.get().snapshot.players.find((p) => p.userId === result.attackerId);
      if (them?.weaponKind === 'FIREARM') {
        const dir = new THREE.Vector3(them.facing.x, 0, them.facing.z);
        if (dir.lengthSq() > 1e-6) {
          dir.normalize();
          muzzleFlashes.flash(
            new THREE.Vector3(opponentDrawn.x, MUZZLE_HEIGHT, opponentDrawn.z).addScaledVector(dir, MUZZLE_FORWARD),
            dir,
          );
        }
        weaponSound?.shoot(them.weaponKey, them.weaponAmmoType);
      } else if (them?.weaponKind === 'MELEE') {
        weaponSound?.swing();
      }
    }

    if (!result.hit) return;
    const targetIsSelf = result.targetId === selfId;
    const at = targetIsSelf
      ? sim.getSnapshot().player.position
      : { x: opponentDrawn.x, z: opponentDrawn.z };
    floaters.spawn({
      text: `-${Math.round(result.damage)}`,
      tone: targetIsSelf ? 'damage-taken' : 'damage-dealt',
      emphasis: result.crit,
      x: at.x,
      y: MUZZLE_HEIGHT,
      z: at.z,
    });
    if (targetIsSelf) playerAnimator.playHitReact();
    else opponentAnimator?.playHitReact();
  });
  cleanups.push(unsubAttack);

  const spawnHeal = (amount: number, targetIsSelf: boolean): void => {
    if (amount <= 0) return;
    const at = targetIsSelf
      ? sim.getSnapshot().player.position
      : { x: opponentDrawn.x, z: opponentDrawn.z };
    floaters.spawn({
      // The brief asks for a bare pair of `+` marks on someone else's body and
      // a real number on your own — you are told how much you healed, they are
      // only told that you did.
      text: targetIsSelf ? `+${Math.round(amount)}` : '+ +',
      tone: 'heal',
      emphasis: false,
      x: at.x,
      y: MUZZLE_HEIGHT,
      z: at.z,
    });
  };

  // Two heal sources, because they answer different questions. The room's
  // broadcast is the in-fight one and is inert until a `healResult` exists to
  // send (there is no in-fight kit use yet); the health-sheet delta is the live
  // one and covers passive regen, which is lazy and therefore has no event of
  // its own to listen for. See `state/heal-feed.ts`.
  const unsubHeal = onHealResult((result) => spawnHeal(result.amount, result.targetId === selfId));
  cleanups.push(unsubHeal);

  // The room sends this to the shooter alone, so it is always OUR trigger that
  // clicked — no `targetId` to check. This is the authoritative path; the
  // local guard in `fireAttack` only exists to remove the round-trip delay.
  cleanups.push(onOutOfAmmo(() => reportOutOfAmmo()));

  const healWatcher = createHealWatcher();
  const unsubHealth = gameStateStore.subscribe((s) => {
    const healed = healWatcher(s.health);
    if (healed) spawnHeal(healed.amount, true);
  });
  cleanups.push(unsubHealth);

  // ---- input ----
  const keys = new Set<string>();
  let dragging = false;
  let lastPointerX = 0;

  const onKeyDown = (e: KeyboardEvent): void => {
    // Space fires while aiming — see `state/control-settings.ts`. This is the
    // view where it actually decides matches: holding the right button on a
    // trackpad is a two-finger press with no finger left over to click with, so
    // without this a laptop player cannot attack in a duel at all.
    //
    // `preventDefault` stops Space scrolling the page underneath the fight.
    if (isFireKey(e)) {
      e.preventDefault();
      if (controls.aiming) fireAttack();
      return;
    }
    // ─── The hotbar's keyboard half ────────────────────────────────────────
    // Checked BEFORE the movement set, so a digit never also lands in `keys`.
    // `hotbarIndexForKey` rejects modified presses on its own (Ctrl+1 is a tab
    // switch), and `trigger` returns false for a dead cell — which is why the
    // `preventDefault` is conditional: an unbound digit should keep whatever
    // the browser does with it.
    const hotbarIndex = hotbarIndexForKey(e);
    if (hotbarIndex !== null) {
      if (hotbar.trigger(hotbarIndex)) e.preventDefault();
      return;
    }
    keys.add(keyName(e));
    if (e.key === 'Escape') hud.toggleMenu();
    controls.handleKeyDown(e);
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    keys.delete(keyName(e));
    controls.handleKeyUp(e);
  };

  const onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0 || e.target !== renderer.domElement) return;
    if (controls.aiming) {
      // LMB while aiming IS the attack here — the settlement's
      // `attemptAttack()` no-op finally has a room to send to.
      fireAttack();
      return;
    }
    dragging = true;
    lastPointerX = e.clientX;
    container.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent): void => {
    if (dragging) {
      const dx = e.clientX - lastPointerX;
      lastPointerX = e.clientX;
      cam.rotate(dx);
    }
  };
  const onPointerUp = (e: PointerEvent): void => {
    dragging = false;
    if (container.hasPointerCapture(e.pointerId)) container.releasePointerCapture(e.pointerId);
  };
  // No zoom in a duel — the orbit is pinned (`ARENA_CAMERA`). The wheel is
  // still swallowed so it cannot scroll the page under the canvas.
  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
  };

  function fireAttack(): void {
    // Frozen players cannot act. The room enforces this too; refusing here
    // keeps the animation and the sound from firing on a swing the server is
    // about to discard.
    if (battlegroundStateStore.get().snapshot.freezeRemaining > 0) return;

    // ─── Dry weapons are refused HERE as well as in the room ───────────────
    // Not belt-and-braces for its own sake: the round-trip is ~50-100 ms, and
    // a flash-plus-crack that plays and is then contradicted by an OUT OF
    // AMMO notice is worse feedback than no flash at all. The room is still
    // the authority — it refuses the same shot and sends `outOfAmmo`, which
    // is what drives the notice on the paths this check cannot see (a stale
    // client, a round spent between two patches).
    const held = selfPlayer();
    // The dead do not shoot. The room ignores it anyway (the match has
    // concluded and the connection is closing), but a local muzzle flash and
    // a gunshot over your own corpse is exactly the "nothing happened" feel
    // that made being downed unreadable in the first place.
    if ((held?.hp ?? 1) <= 0) return;
    if (held?.ammoRemaining === 0) {
      reportOutOfAmmo();
      return;
    }

    const { facing } = sim.getSnapshot().player;
    sendAttack({ x: Math.sin(facing), y: 0, z: Math.cos(facing) });
    playerAnimator.playAttack();

    // ─── The visible and audible halves of a shot ──────────────────────────
    // Fired from the LOCAL press, not from the server's `attackResult`: a
    // muzzle flash that waits for a round trip reads as the gun being broken.
    // The room may still reject the shot on cadence, in which case the flash
    // is a frame of feedback for a trigger pull that genuinely happened.
    //
    // The weapon comes from the ROOM's synced state where it has one — the
    // hotbar lets a fighter switch mid-duel, so the equipment sheet stops
    // being the truth from the first keypress. It falls back to the sheet
    // only before the first patch has landed.
    const weapon = heldWeaponDescriptor();
    if (weapon.kind === 'FIREARM') {
      const { position } = sim.getSnapshot().player;
      const dir = new THREE.Vector3(Math.sin(facing), 0, Math.cos(facing));
      const muzzle = new THREE.Vector3(position.x, MUZZLE_HEIGHT, position.z)
        .addScaledVector(dir, MUZZLE_FORWARD);
      muzzleFlashes.flash(muzzle, dir);
      shotFeedback.kick();
      // Same gate as the flash and deliberately in the same place: a firearm
      // that flashes and one that cracks must never be able to disagree.
      weaponSound?.shoot(weapon.itemKey, weapon.ammoType);
    } else if (weapon.kind === 'MELEE') {
      weaponSound?.swing();
    }
    // UNARMED is deliberately silent — there is no bare-hands recording, and
    // `weapon-sound.ts` would log a dev warning for a weapon that is working
    // exactly as intended.
  }

  /** Our own row in the room's synced state, or null before the first patch. */
  function selfPlayer(): RoomPlayer | null {
    return battlegroundStateStore.get().snapshot.players.find((p) => p.userId === selfId) ?? null;
  }

  /**
   * What is in our hands, for the flash and the recording.
   *
   * Room state first, equipment sheet second. The sheet is only right until
   * the player presses a hotbar key; the room is right always, but is empty
   * for the first patch or two after joining.
   */
  function heldWeaponDescriptor(): { kind: string; itemKey?: string; ammoType?: string } {
    const mine = selfPlayer();
    if (mine?.weaponKind) {
      return { kind: mine.weaponKind, itemKey: mine.weaponKey, ammoType: mine.weaponAmmoType };
    }
    const equipped = gameStateStore.get().equipment?.slots;
    const fallback = [equipped?.PRIMARY, equipped?.SECONDARY]
      .find((eq) => eq?.def.kind === 'FIREARM' || eq?.def.kind === 'MELEE');
    if (!fallback) return { kind: 'UNARMED' };
    return { kind: fallback.def.kind, itemKey: fallback.def.key, ammoType: fallback.def.ammoType ?? undefined };
  }

  /**
   * The empty-trigger feedback, in one place because it has three halves that
   * must never be able to fire separately: the error sound, the red cell, and
   * the red notice in the middle of the screen.
   *
   * Janeks: *"play error sound, highlight the selected UI slot in RED and show
   * a red notice text 'OUT OF AMMO' in the middle."*
   */
  function reportOutOfAmmo(): void {
    uiErrorSound();
    hotbar.flashError('Out of ammo');
  }

  container.addEventListener('pointerdown', onPointerDown);
  container.addEventListener('pointermove', onPointerMove);
  container.addEventListener('pointerup', onPointerUp);
  container.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  // Held keys are dropped when their keyup can be lost — see key-input.ts.
  cleanups.push(trackHeldKeys(keys));
  cleanups.push(
    () => container.removeEventListener('pointerdown', onPointerDown),
    () => container.removeEventListener('pointermove', onPointerMove),
    () => container.removeEventListener('pointerup', onPointerUp),
    () => container.removeEventListener('wheel', onWheel),
    () => window.removeEventListener('keydown', onKeyDown),
    () => window.removeEventListener('keyup', onKeyUp),
  );

  if (import.meta.env.DEV) {
    const w = window as DuelDevWindow;
    w.__duel = {
      self: () => selfPlayer(),
      opponent: () => battlegroundStateStore.get().snapshot.players.find((p) => p.userId !== selfId) ?? null,
      freezeRemaining: () => battlegroundStateStore.get().snapshot.freezeRemaining,
      opponentDrawn: () => ({ x: opponentDrawn.x, z: opponentDrawn.z }),
      opponentMoving: () => opponentMoving,
      showResult: (outcome) => hud.showResult(outcome),
      hotbar: () => buildHotbarSlots(gameStateStore.get().equipment, gameStateStore.get().inventory),
      outOfAmmoShown: () => !!hotbar.root.querySelector('.bv-hotbar-notice.is-shown'),
      /** The heal cast bar's live state, for the channel assertions. */
      healing: () => ({
        shown: !!hotbar.root.querySelector('.bv-hotbar-cast.is-shown'),
        remainingMs: selfPlayer()?.healingRemaining ?? 0,
        label: hotbar.root.querySelector('.bv-hotbar-cast-label')?.textContent ?? '',
      }),
      /**
       * The spectator crowd, as bone positions.
       *
       * Exists because the crowd being FROZEN leaves no trace anywhere else:
       * `attachWorldModels` built the animators from the day the arena
       * shipped and nothing called `update()` on them until 2026-09-19, so
       * every onlooker held the GLB's rest pose with no error, no warning and
       * nothing in the DOM. A probe can sample this twice and see whether a
       * hand actually moved — which is the only honest way to assert that a
       * cheering crowd is cheering.
       *
       * Returns every skinned body in the scene, the fighters included;
       * callers filter by `radius` (spectators live outside the 22.12 m fence).
       */
      crowd: () => {
        const rows: { radius: number; hand: [number, number, number] }[] = [];
        const bodyAt = new THREE.Vector3();
        const handAt = new THREE.Vector3();
        sceneHandles.scene.traverse((o) => {
          const skinned = o as THREE.SkinnedMesh;
          if (!skinned.isSkinnedMesh) return;
          const bone = skinned.skeleton?.bones.find((b) => /hand/i.test(b.name) && /R$/i.test(b.name));
          if (!bone) return;
          skinned.getWorldPosition(bodyAt);
          bone.getWorldPosition(handAt);
          rows.push({
            radius: Math.hypot(bodyAt.x, bodyAt.z),
            hand: [handAt.x, handAt.y, handAt.z],
          });
        });
        return rows;
      },
      heldWeaponModel: () => {
        // Stamped by `updateWeaponVisual` at attach time — see there for why
        // the scene graph carries no other route back to the item key.
        let found: { key: string } | null = null;
        sceneHandles.player.traverse((o) => {
          if (found || !o.userData?.weaponKey) return;
          found = { key: String(o.userData.weaponKey) };
        });
        return found;
      },
    };
    cleanups.push(() => { delete w.__duel; });
  }

  // The scene fog is measured from the CAMERA (28 → 74 m, `buildScene`), tuned
  // for the 30 m orbit. A narrow screen flies a longer orbit to keep the bots'
  // sight radius in frame (`orbitForGroundRadius`) — ~58 m on a 9:19.5 phone —
  // which put the whole arena inside the fog. Slide the fog out by the extra.
  const fog = sceneHandles.scene.fog instanceof THREE.Fog ? sceneHandles.scene.fog : null;
  const fogBase = fog ? { near: fog.near, far: fog.far } : null;
  const resize = (): void => {
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    renderer.setSize(w, h, false);
    cam.setAspect(w / h);
    if (fog && fogBase) {
      const extra = Math.max(0, cam.distanceValue - DEFAULT_CAMERA.maxDistance);
      fog.near = fogBase.near + extra;
      fog.far = fogBase.far + extra;
    }
  };
  const ro = new ResizeObserver(resize);
  ro.observe(container);
  cleanups.push(() => ro.disconnect());
  resize();

  let raf = 0;
  let last = performance.now();

  /**
   * "My map has finished loading" — sent once, from inside the first rendered
   * frame.
   *
   * ─── The signal is a painted frame, not a loaded file ───────────────────
   * It used to fire from the settlement the instant the LOBBY opened, with a
   * comment explaining that there was no scene to wait on yet because this
   * file did not exist. It does now, so the report belongs behind it.
   *
   * The first frame is the honest line: the ground, the lights, the camera
   * and both bodies are up and the player is looking at the arena. Individual
   * GLBs may still be streaming in — `attachWorldModels` parents them in as
   * they arrive — and that is precisely what the eight-second hold is FOR.
   * Waiting for every model would push the report behind the slowest asset on
   * the map and spend the join window instead.
   */
  let reportedLoaded = false;

  const frame = (now: number): void => {
    raf = requestAnimationFrame(frame);
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;

    const duel = battlegroundStateStore.get();
    const frozen = duel.snapshot.freezeRemaining > 0;

    // ─── Downed is a real state, not just a result card ────────────────────
    //
    // Janeks, in a duel: *"when donwed - i am not downed, i can run around."*
    // The room concluded the match and the card came up, but the client kept
    // running its own sim — the player walked around their own corpse's
    // health bar until they clicked away. Nothing was wrong server-side; the
    // duel was over and every input was being ignored by a room that had
    // already disconnected. It just looked like nothing had happened.
    //
    // Driven off the SYNCED hp rather than off the result payload: the room
    // zeroes `PlayerState.hp` in the same patch it resolves the killing blow,
    // and the conclude round-trip (Prisma writes, reward application) lands
    // afterwards. Waiting for the card means a visible window of running
    // around dead, which is the bug.
    const selfRow = duel.snapshot.players.find((p) => p.userId === selfId);
    const downed = (selfRow?.hp ?? 1) <= 0;
    if (downed && !deathPlayed) {
      deathPlayed = true;
      playerAnimator.playDeath();
    }

    // ─── The freeze puts you back on your spawn, and holds you there ───────
    //
    // Janeks: *"After matchmaking is done - we spawn into battleground map and
    // run around waiting for opponent. When opponent is connected we should be
    // frozen and Teleported to the spawn zone!"*
    //
    // The two positions had genuinely diverged. `BattlegroundRoom.handleMove`
    // DROPS every move message while the freeze runs ("nobody can move",
    // enforced server-side and not only in this input loop), so the room kept
    // both fighters on their authored spawn points while this client's own sim
    // walked wherever the player walked. At the bell the next move message
    // snapped the room to wherever that was — a free repositioning during the
    // countdown, invisible to the player it happened to and to the one it
    // happened AGAINST.
    //
    // So the server's position wins for exactly as long as the server is the
    // one holding it. Every frame, not once on the transition: a single
    // teleport would be undone by the held momentum and by any key still down.
    // `sim.setPlayerPosition` clears the velocity with the position for the
    // same reason. Facing is deliberately NOT forced — the camera, the aim and
    // the animator stay live through the countdown so the player can look
    // around and line up before it starts.
    if (frozen && selfRow) sim.setPlayerPosition(selfRow.position);

    controls.updateAim(sim.getSnapshot().player.position);

    const input = {
      // Movement is zeroed during the freeze rather than the whole sim being
      // skipped: the camera, the animator and the aim direction must all stay
      // live so the player can look around and line up before the bell.
      // `frozen || downed` — the pre-duel countdown and death both mean "you
      // do not move", and they are the only two things that do.
      moveX: frozen || downed ? 0 : (keys.has('d') || keys.has('arrowright') ? 1 : 0) - (keys.has('a') || keys.has('arrowleft') ? 1 : 0),
      moveZ: frozen || downed ? 0 : (keys.has('w') || keys.has('arrowup') ? 1 : 0) - (keys.has('s') || keys.has('arrowdown') ? 1 : 0),
      aimYaw: controls.simAimYaw,
      gait: controls.gait,
      crouched: controls.crouched,
    };
    sim.update(dt, input, cam.yawValue);

    const snap = sim.getSnapshot();
    sceneHandles.player.position.set(snap.player.position.x, 0, snap.player.position.z);
    sceneHandles.player.rotation.y = snap.player.facing;
    playerShadow.position.set(snap.player.position.x, playerShadow.position.y, snap.player.position.z);
    playerShadow.rotation.y = snap.player.facing;
    cam.update(snap.player.position);
    playerAnimator.update(dt, {
      moving: snap.player.moving,
      gait: controls.gait,
      crouched: controls.crouched,
      aiming: controls.aiming,
      dialogOpen: false,
    });
    // The crowd. `attachWorldModels` has always built these animators here,
    // but until 2026-09-19 nothing ticked them — an `AnimationMixer` that is
    // never advanced applies NOTHING, so all 37 onlookers stood frozen in the
    // GLB's own rest pose for as long as the arena has existed. It read as
    // "the crowd is just static scenery" rather than as a bug, which is why
    // it survived; only `index.ts` (the settlement) called update.
    // `null` because a battleground has no dialog: nobody here is talking.
    npcAnimators.update(dt, null);

    // ---- stream our position up ----
    if (!frozen && !downed) {
      sendMove(
        { x: snap.player.position.x, y: 0, z: snap.player.position.z },
        { x: Math.sin(snap.player.facing), y: 0, z: Math.cos(snap.player.facing) },
      );
    }

    // ---- draw the opponent from the room's state ----
    const players = duel.snapshot.players;
    const them = players.find((p) => p.userId === opponentId) ?? players.find((p) => p.userId !== selfId);
    if (them) {
      // The body can only be requested once the room has said whose it is.
      loadOpponentBody(them.presetId);

      if (!opponentSeen) {
        opponentSeen = true;
        opponentDrawn.set(them.position.x, 0, them.position.z);
        opponentRoot.rotation.y = Math.atan2(them.facing.x, them.facing.z);
        opponentShadow.visible = true;
      }

      // ─── The opponent is always drawn, even behind cover ─────────────────
      //
      // A client-side occlusion rule lived here briefly and was removed the
      // same session, because it made the game unplayable and the measurement
      // says why. Sampling random pairs of positions in the two arenas, two
      // fighters have a clear 2D ground-level sight line only 16% of the time
      // in the pit and 8% in the scrapyard — so hiding the body whenever that
      // line is blocked hid the opponent almost always. Janeks, in a live
      // duel: *"I am in a pvp session and i dont see the enemy anywhere."*
      //
      // The model was the problem, not the threshold. Sight is tested as a
      // flat segment between two ground positions (see `sim/vision.ts`), which
      // is a fair approximation of what a BOT standing in the yard can see —
      // and a poor one for what the PLAYER can see, because the camera is
      // behind and well above them and looks straight over most of the
      // scenery. The two questions genuinely have different answers.
      //
      // So the server-side half stays (bots lose you behind cover and hunt a
      // stale belief — that is the whole feature) and the client simply draws
      // what is there. The asymmetry is real and is stated rather than hidden:
      // you can see a bot that cannot currently see you. Doing this properly
      // would mean real 3D occlusion against the camera ray, which is a
      // rendering feature in its own right and not something to infer from a
      // 2D footprint table.
      opponentRoot.visible = true;
      opponentShadow.visible = true;
      // ONE epsilon on both sides. Two different thresholds (set above 1e-4,
      // clear only at exactly 0) leave every value between them un-handled, so
      // an opponent who stops anywhere but on a perfectly identical patch keeps
      // running a walk cycle on the spot forever.
      //
      // ─── Compared per PATCH, held across the frames between them ─────────
      // Patches land at the room's patch rate (Colyseus' 50 ms), frames at 60+
      // Hz, so two frames in three see the same position as the last one. A
      // per-frame "did it move" therefore read false most of the time on a
      // walking bot, and the animator crossfaded run -> idle -> run about 38
      // times a second (measured in a live duel: moving on 31% of frames).
      // Janeks: *"the bot was jittering (like shaking all the time, until saw
      // me)"* — it stopped once the bot engaged, because an engaged bot mostly
      // stands in its firing band. A position change starts a hold that covers
      // a dropped patch or two; only a hold that runs out means "stopped".
      const moved = Math.hypot(them.position.x - opponentLastPos.x, them.position.z - opponentLastPos.z);
      if (moved > MOVE_EPSILON) opponentMoveHold = OPPONENT_MOVE_HOLD_S;
      else opponentMoveHold = Math.max(0, opponentMoveHold - dt);
      opponentMoving = opponentMoveHold > 0;
      opponentLastPos = them.position;

      // Exponential smoothing — frame-rate independent, see the constant.
      const alpha = 1 - Math.pow(0.5, dt / OPPONENT_SMOOTH_HALFLIFE);
      opponentDrawn.x += (them.position.x - opponentDrawn.x) * alpha;
      opponentDrawn.z += (them.position.z - opponentDrawn.z) * alpha;
      opponentRoot.position.set(opponentDrawn.x, 0, opponentDrawn.z);
      // Turned with the same half-life as the position. The room changes a
      // bot's heading once per 250 ms decision, so a direct copy snapped the
      // body round in visible jerks on top of the gait flicker above.
      const yawTarget = Math.atan2(them.facing.x, them.facing.z);
      let dYaw = yawTarget - opponentRoot.rotation.y;
      dYaw = Math.atan2(Math.sin(dYaw), Math.cos(dYaw));
      opponentRoot.rotation.y += dYaw * alpha;
      opponentShadow.position.set(opponentDrawn.x, opponentShadow.position.y, opponentDrawn.z);
      // The opponent falls too — the duel has a loser either way, and a
      // winner watching a corpse jog on the spot is the same bug from the
      // other side.
      if (them.hp <= 0 && !opponentDeathPlayed) {
        opponentDeathPlayed = true;
        opponentAnimator?.playDeath();
      }
      opponentAnimator?.update(dt, {
        moving: opponentDeathPlayed ? false : opponentMoving,
        gait: 'run',
        crouched: false,
        aiming: false,
        dialogOpen: false,
      });
    }

    // ---- the aim arc ----
    //
    // ─── Drawn from the ROOM, not from the equipment sheet ──────────────────
    // The sheet's `aimArc` is the weapon the player LAUNCHED with. The hotbar
    // switches weapons mid-duel, so from the first keypress the sheet draws a
    // cone the server has stopped resolving against — and `aim-arc.ts`'s own
    // header is explicit that this must never happen: "an arc drawn wider than
    // the one the server resolves promises hits that cannot land, and one
    // drawn narrower hides hits that do."
    //
    // The sheet stays as the fallback for the frames before the first patch
    // lands, and for a server too old to sync the geometry.
    const self = players.find((p) => p.userId === selfId);
    const sheetArc = gameStateStore.get().equipment?.aimArc ?? null;
    const arc = self?.arcLength !== undefined && self.arcWidth !== undefined
      ? {
        kind: (self.weaponKind ?? 'UNARMED') as 'MELEE' | 'FIREARM' | 'UNARMED',
        arcLength: self.arcLength,
        arcWidth: self.arcWidth,
        itemKey: self.weaponKey ?? null,
      }
      : sheetArc;
    aimArc.update({
      visible: controls.aiming && !!arc,
      arc,
      x: snap.player.position.x,
      z: snap.player.position.z,
      facing: snap.player.facing,
    });

    // ---- the hotbar reads the held weapon from the room, never optimistically ----
    //
    // A cell that lit up on the keypress and then had to un-light when the
    // room refused the swap would flicker on exactly the loadouts where the
    // refusal is correct (an empty SECONDARY). The room's answer IS the state.
    if (self?.weaponSlot) {
      hotbar.setActive(self.weaponSlot);
      hotbar.setAmmo(self.ammoRemaining);
    }

    // ---- the heal cast bar, timed by the SERVER ----
    // `healingRemaining` reaching 0 is the room saying the channel is over —
    // by completion or by cancellation, and `onHealCancelled` has already
    // distinguished the two. Clearing here rather than on a local timer is
    // what keeps the bar from finishing before or after the heal does.
    if (healing) {
      const remaining = self?.healingRemaining ?? 0;
      if (remaining <= 0) {
        healing = null;
        hotbar.setHealing(null);
      } else {
        hotbar.setHealing({ ...healing, remainingMs: remaining });
      }
    }

    // ---- HP / armour bars, floating numbers ----
    bars.update([
      ...(self ? [barFor(self, snap.player.position.x, snap.player.position.z)] : []),
      ...(them ? [barFor(them, opponentDrawn.x, opponentDrawn.z)] : []),
    ]);
    floaters.update(dt, cam.camera);
    // A muzzle flash is ONE frame of light (MUZZLE_FLASH_SECONDS = 0.055) and
    // the pool hides it from here. Without this line the sprites were shown
    // and never hidden: four of them hung in the air at the world positions
    // they were fired from, and a bot that kept moving left a trail of them.
    // Janeks: *"Bot shot muzzleflashes (sprites) are staying in the air after
    // animation end if bot is moving."* Same class of bug, in the same frame
    // loop, as the NPC animators nobody ticked — see npc-animator-tick.test.ts.
    muzzleFlashes.update(dt);

    sceneHandles.setHovered(null, false);
    sceneHandles.updateAmbience(dt, reducedMotionMql.matches);
    sceneHandles.updateGrass(dt, cam.yawValue);
    // Authored fire/smoke (`WorldData.spriteSpawns`), advanced the way the
    // settlement advances it. Without this call every placed campfire renders
    // as a frozen first frame — lit, in place, and not burning. Caught by
    // `sprite-fx-tick.test.ts`, which discovers a view by the grass it ticks.
    sceneHandles.spriteSpawns.update(dt);
    nameTags.update(snap.player.position, snap.actors);

    // ─── The hold, from whichever clock currently owns it ──────────────────
    // Before the join there is no room and so no `freezeRemaining`: the
    // countdown belongs to the arena lobby, and is derived from its phase
    // deadline exactly as the lobby modal derived it (`arena-state.ts`'s
    // header: nothing on screen ever counts down, everything is computed from
    // a snapshot plus elapsed wall time). After the join the room's copy
    // takes over — it is the one the server actually enforces.
    //
    // Without this the new pre-join stretch would show an empty HUD over a
    // live arena, which reads as a duel that has already started and is not
    // accepting input.
    const arena = arenaStateStore.get();
    const preJoin = duel.status !== 'joined';
    const lobbyFreeze = preJoin && arena.view.phase === 'COUNTDOWN' ? remainingMs(arena) : 0;

    hud.update({
      freezeRemaining: preJoin ? lobbyFreeze : duel.snapshot.freezeRemaining,
      timeRemaining: duel.snapshot.timeRemaining,
      self: self ?? null,
      opponent: them ?? null,
      status: duel.status,
      // LOBBY is "I am here, the other one is still loading" — a wait with no
      // deadline worth showing, but emphatically something to SAY.
      waitingFor: preJoin && arena.view.phase === 'LOBBY' ? `Waiting for ${match.opponent.username}` : null,
    });

    // The shake shifts the projection for this render only; the camera every
    // raycast and the aim read is cleared again straight after.
    shotFeedback.beforeRender(cam.camera, container.clientWidth, container.clientHeight);
    renderer.render(sceneHandles.scene, cam.camera);
    shotFeedback.afterRender(cam.camera);

    if (!reportedLoaded) {
      reportedLoaded = true;
      reportArenaLoaded();
    }
  };
  raf = requestAnimationFrame(frame);

  return () => {
    torndown = true;
    cancelAnimationFrame(raf);
    void leaveDuel();
    for (const fn of cleanups) fn();
    sceneHandles.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
    renderer.domElement.remove();
    removeStyles();
    container.remove();
  };
}

function barFor(p: RoomPlayer, x: number, z: number): {
  x: number; z: number; hp: number; maxHp: number; armor: number; maxArmor: number;
} {
  return {
    x,
    z,
    hp: p.hp,
    // A missing max is not a reason to draw nothing — fall back to the current
    // value so the bar reads full rather than dividing by zero.
    maxHp: p.maxHp && p.maxHp > 0 ? p.maxHp : Math.max(p.hp, 1),
    armor: p.armor ?? 0,
    maxArmor: p.maxArmor && p.maxArmor > 0 ? p.maxArmor : 0,
  };
}

function aspectOf(el: HTMLElement): number {
  const w = el.clientWidth || 1;
  const h = el.clientHeight || 1;
  return w / h;
}
